import { Fragment, useEffect, useState } from "react";
import ConfirmationDialog from "@hudson/ui/components//Dialogs/Confirmation";
import Dialog from "@hudson/ui/components/Dialogs/Simple";
import NewUser from "@components/User/New";
import Profile from "@components/User/Profile";
import { useRouter } from "next/router";
import LoginOrCreate from "@components/User/LoginOrCreate";
import SendReset from "@components/User/SendReset";
import Session from "@lib/session";
import { Menu, Transition } from "@headlessui/react";
import classNames from "classnames";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import styles from "@hudson/ui/styles/Form.module.css";
import { camelToSnakeCase } from "@hudson/ui/lib/utils";
import useContent from "@lib/use_content";
import useConfig from "@lib/use_config";
import { useColorsQuery, useUserProfileQuery } from "@graphql/api";

export default function LoggedIn(): JSX.Element | null {
  const {
    data: profileData,
    loading: profileLoading,
    error,
  } = useUserProfileQuery({
    variables: { id: Session.getBooking().profiles.user || 0 },
  });
  const router = useRouter();
  const [showDialog, setShowDialog] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [dialogPage, setDialogPage] = useState("profile");
  const { data: clrData } = useColorsQuery();
  const { data: cfgData } = useConfig(["hideUserProfileActions"]);
  const { data: cData } = useContent([
    "beConfirmDialogDefaultCancelButton",
    "beLogoutButton",
    "beLogoutConfirmationButton",
    "beLogoutConfirmationMsg",
    "beLogoutConfirmationTitle",
    "beProfileAccountButton",
  ]);

  // If we've been directed to open the login popup via url param then do so.
  useEffect(() => {
    if (router.query.open_profile) setShowDialog(true);
  }, []);

  let DialogComp;
  let maxWidthClass = "max-w-md";
  switch (dialogPage) {
    case "loginOrCreate":
      DialogComp = LoginOrCreate;
      break;
    case "newUser":
      DialogComp = NewUser;
      break;
    case "profile":
      DialogComp = Profile;
      maxWidthClass = "max-w-4xl";
      break;
    case "sendReset":
      DialogComp = SendReset;
      break;
  }

  async function logout() {
    await Session.clearSession();
    window.location.href = `${router.basePath}/${router.query.siteId}/book`;
  }

  function showProfileDialog() {
    setDialogPage("profile");
    setShowDialog(true);
  }

  return (
    <>
      <Dialog
        maxWidthClass={maxWidthClass}
        show={showDialog}
        onHide={() => setShowDialog(false)}
        pagePath={`/user/${camelToSnakeCase(dialogPage)}`}
      >
        <DialogComp onPageChange={(page) => setDialogPage(page)} />
      </Dialog>
      <ConfirmationDialog
        cancelLabel={cData?.content.beConfirmDialogDefaultCancelButton}
        confirmLabel={cData?.content.beLogoutConfirmationButton}
        message={cData?.content.beLogoutConfirmationMsg}
        onConfirm={logout}
        onHide={() => setShowLogout(false)}
        show={showLogout}
        title={cData?.content.beLogoutConfirmationTitle}
      />

      <Menu as="div" className="shrink-0 relative ml-4">
        <div>
          <Menu.Button
            className={classNames(styles.link, "bg-white rounded-md flex focus:outline-none ")}
          >
            <div className="p-1 flex items-center space-x-2 text-lg text-gray-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke={clrData?.colors.primaryColor}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>{profileData?.userProfile?.contactInfo?.name}</span>
              {cfgData?.config.hideUserProfileActions ? null : (
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              )}
            </div>
          </Menu.Button>
        </div>
        {cfgData?.config.hideUserProfileActions ? null : (
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black/5 py-1 focus:outline-none z-50">
              <Menu.Item key="account">
                {({ active }) => (
                  <a
                    href="#"
                    onClick={showProfileDialog}
                    className={classNames(
                      active ? "bg-gray-100" : "",
                      "block py-2 px-4 text-base text-gray-700",
                    )}
                  >
                    {cData?.content.beProfileAccountButton}
                  </a>
                )}
              </Menu.Item>
              <Menu.Item key="logout">
                {({ active }) => (
                  <a
                    href="#"
                    className={classNames(
                      active ? "bg-gray-100" : "",
                      "block py-2 px-4 text-base text-gray-700",
                    )}
                    onClick={() => setShowLogout(true)}
                  >
                    {cData?.content.beLogoutButton}
                  </a>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        )}
      </Menu>
    </>
  );
}
